// Import Wizard helpers shared by every import type. The API reports row errors as
// "Column: message" strings and staged rows as json_data objects keyed by the file's header.
import service from 'Api/service';
import { getActionCableConsumer } from '../getActionCableConsumer';

export type WizardType =
  | 'item'
  | 'accessory'
  | 'purchase'
  | 'bundle'
  | 'customer'
  | 'company'
  | 'venue';

export const WIZARD_TYPES: WizardType[] = [
  'item',
  'accessory',
  'purchase',
  'bundle',
  'customer',
  'company',
  'venue',
];

// Legacy modal / page type names → wizard import type.
const TYPE_ALIASES: Record<string, WizardType> = {
  item: 'item',
  items: 'item',
  product: 'item',
  products: 'item',
  accessory: 'accessory',
  accessories: 'accessory',
  purchase: 'purchase',
  purchases: 'purchase',
  addon: 'purchase',
  addons: 'purchase',
  add_on: 'purchase',
  add_ons: 'purchase',
  bundle: 'bundle',
  bundles: 'bundle',
  customer: 'customer',
  customers: 'customer',
  company: 'company',
  companies: 'company',
  venue: 'venue',
  venues: 'venue',
};

export const wizardTypeFor = (type = ''): WizardType | null =>
  TYPE_ALIASES[String(type).toLowerCase()] || null;

// API resource that serves each type's example CSV (/api/<resource>/example_csv).
export const EXAMPLE_RESOURCES: Record<string, string> = {
  item: 'products',
  accessory: 'accessories',
  purchase: 'add_ons',
  bundle: 'bundles',
  customer: 'customers',
  company: 'companies',
  venue: 'venues',
};

export const PRETTY_NAMES: Record<string, string> = {
  item: 'Items',
  accessory: 'Accessories',
  purchase: 'Purchases',
  bundle: 'Bundles',
  customer: 'Customers',
  company: 'Companies',
  venue: 'Venues',
};

export const LIST_PATHS: Record<string, string> = {
  item: '/inventory/items',
  accessory: '/inventory/accessories',
  purchase: '/inventory/purchases',
  bundle: '/inventory/bundles',
  customer: '/contacts/customers',
  company: '/contacts/companies',
  venue: '/contacts/venues',
};

export interface RowError {
  column: string;
  message: string;
}

export interface StagedRowPayload {
  id: number;
  line?: number | null;
  has_errors?: boolean;
  json_data: string | Record<string, unknown>;
}

export interface StagedRow {
  [column: string]: unknown;
  id: number;
  line?: number | null;
  errors: RowError[];
  isEditing?: string | false;
}

export interface ImportProgressPayload {
  id: number;
  status: string;
  current_count: number;
  finish_count: number;
  response: Record<string, unknown> & {
    message?: string;
    errors?: Array<[number | string, { error: string }]>;
  };
}

export const parseErrors = (errors: string[] = []): RowError[] =>
  errors.map((error) => {
    const at = error.indexOf(': ');
    return at > 0
      ? { column: error.slice(0, at), message: error.slice(at + 2) }
      : { column: '', message: error };
  });

export const buildFromJson = (row: StagedRowPayload): StagedRow => {
  const data = (
    typeof row.json_data === 'string'
      ? JSON.parse(row.json_data)
      : row.json_data || {}
  ) as Record<string, unknown>;
  return {
    ...data,
    errors: parseErrors((data.errors as string[]) || []),
    id: row.id,
    line: row.line,
  };
};

export const getCellErrors = (
  errors: RowError[] = [],
  columnName: string
): string =>
  errors
    .filter((error) => error.column === columnName)
    .map((error) => error.message)
    .join('; ');

export const rowLevelErrors = (
  errors: RowError[] = [],
  columns: string[] = []
): string[] =>
  errors
    .filter((error) => !columns.includes(error.column))
    .map((error) =>
      error.column ? `${error.column}: ${error.message}` : error.message
    );

// Downloads an API-served example CSV (needs the auth headers a plain link cannot carry).
export const downloadExampleCsv = (importType: string): Promise<void> => {
  const resource = EXAMPLE_RESOURCES[importType];
  return service
    .getBlob(`${import.meta.env.VITE_API_DOMAIN}/api/${resource}/example_csv`)
    .then((response: { data: BlobPart }) => {
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'text/csv' })
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = `${resource === 'products' ? 'items' : resource}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    });
};

// Follows an ImportProgress on the ProgressChannel until it completes or fails.
// onProgress(percent, data) fires on every push; resolves with the final payload, rejects with it on failure.
export const followImportProgress = (
  importId: number | string,
  onProgress: (percent: number, data: ImportProgressPayload) => void = () =>
    undefined
): Promise<ImportProgressPayload> =>
  new Promise((resolve, reject) => {
    const cable = getActionCableConsumer();
    const subscription = cable.subscriptions.create(
      { channel: 'ProgressChannel', id: importId },
      {
        received: (data: ImportProgressPayload) => {
          const { current_count, finish_count, status } = data;
          onProgress(
            finish_count > 0
              ? Math.min(100, (current_count / finish_count) * 100)
              : 0,
            data
          );
          if (status === 'completed') {
            subscription.unsubscribe();
            resolve(data);
          } else if (status === 'failed') {
            subscription.unsubscribe();
            reject(data);
          }
        },
      }
    );
  });

const helper = { parseErrors, buildFromJson, getCellErrors, rowLevelErrors };

export default {
  VenueHelper: helper,
  CustomerHelper: helper,
  CompanyHelper: helper,
  ItemHelper: helper,
  PurchaseHelper: helper,
  AccessoryHelper: helper,
  BundleHelper: helper,
};
